// Durable React Query cache (offline read-side).
//
// Without this, reads survive offline only via the in-memory gcTime (2h) plus
// the service-worker app-shell precache — so a device cold-started offline (or
// offline > 2h) gets EMPTY dropdowns and can't fill a form. Persisting the
// query cache to IndexedDB means the last-seen reference data (blocks, growers,
// recipes, workers, config, …) is there on boot, so forms work cold-offline.
//
// Freshness is preserved by the app's existing `refetchOnMount: 'always'`:
// online, every query refetches on mount, so the persisted cache is only ever
// a fast offline placeholder that self-heals the moment there's signal.
//
// Safeguards against showing stale/old-shaped data:
//   * maxAge (24h) — entries older than this are dropped on restore.
//   * buster = app version — a version bump invalidates the whole cache.
//   * stale-SCHEMA recovery clears this cache too (see errorReporter), so a
//     breaking deploy can't restore data whose shape no longer matches.

import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { get, set, del } from 'idb-keyval'

// Single IDB key holding the dehydrated cache. Exported so the recovery path
// can wipe exactly this.
export const RQ_PERSIST_KEY = 'kf49-rq-cache'

// Sealed at sign-out for the rest of this page. The persisted cache carries
// the piece-work face roster, and wiping the key is not enough on its own: the
// in-memory cache still holds the roster, and the persister re-dehydrates on
// every cache event (a throttled write may already be queued), so it would be
// written straight back. Blocking at setItem drops those writes too. A reload
// (Login reloads after sign-in) starts unsealed.
let writesSealed = false

const basePersister = createAsyncStoragePersister({
  key: RQ_PERSIST_KEY,
  storage: {
    getItem:    async (k) => (await get<string>(k)) ?? null,
    setItem:    async (k, v) => { if (writesSealed) return; await set(k, v) },
    removeItem: async (k) => { await del(k) },
  },
  // Throttle writes so a busy live board (many queries updating) doesn't thrash
  // IndexedDB on every cache mutation.
  throttleTime: 2_000,
})

// Boot-restore timeout (finding M1, 2026-06-11): PersistQueryClientProvider
// holds EVERY useQuery un-subscribed (isRestoring) until restoreClient
// settles. The fleet's browsers are known to wedge IndexedDB reads after
// backgrounding (the iOS/Samsung IDB errors our Sentry filters swallow) — a
// hung read here meant every page sat on a spinner app-wide until reload,
// with zero telemetry. Racing the restore against a short timer turns that
// into a cold-cache boot: the app proceeds empty and refetchOnMount:'always'
// repopulates everything on the spot. 4s is generous for a single-key IDB
// read (typically <100ms) while barely registering against a real hang.
const RESTORE_TIMEOUT_MS = 4_000
export const queryPersister: typeof basePersister = {
  ...basePersister,
  restoreClient: () => Promise.race([
    basePersister.restoreClient(),
    new Promise<undefined>(resolve => setTimeout(() => {
      console.warn(`queryPersist: restore exceeded ${RESTORE_TIMEOUT_MS}ms — booting with an empty cache`)
      // The E7 rate question this used to report to client_errors is ANSWERED:
      // measured 2026-08-05 at 1,736 bails / 685 sessions / 11 tenants over
      // 14 days — the bail is the COMMON boot path, not the rare hang, and the
      // crumb was 89% of the support error queue. Verdict recorded (support
      // triage, PR #1246 follow-up): late-hydration recovery is worth
      // building; until then this warn is signal enough.
      resolve(undefined)
    }, RESTORE_TIMEOUT_MS)),
  ]),
}

// Wipe the durable cache. Called by the stale-schema recovery (errorReporter)
// so a 42703-style "column does not exist" — i.e. the live bundle's queries no
// longer match what's persisted — busts the durable cache as well as the SW.
/** Sign-out: stop every further write from this page, then wipe the key. */
export async function wipePersistedQueryCacheForSignOut(): Promise<void> {
  writesSealed = true
  await clearPersistedQueryCache()
}

export async function clearPersistedQueryCache(): Promise<void> {
  try { await del(RQ_PERSIST_KEY) } catch { /* best-effort; never throw from a recovery path */ }
}

// Is a query's data safe to persist? Only if it round-trips through JSON with
// no type loss. JSON SILENTLY mangles the dangerous types — Map/Set → `{}`/`[]`
// (prototype gone → a consumer's `.has()`/`.get()` throws at render and trips
// the top-level ErrorBoundary, bricking the device until maxAge), Date → string
// (`.getTime()` throws), and any class instance loses its methods. A cycle would
// make JSON.stringify throw outright. So we deep-scan and refuse to persist a
// query whose data holds any of those anywhere — not just at the top level,
// which is the gap mig-965d63c's `instanceof Map` check left open (a nested
// `{ codeMap: new Map() }` slipped straight through).
//
// Bounded (node-visit budget) so a huge payload can't stall the dehydrate;
// blowing the budget fails closed (don't persist). Those queries are
// refetchOnMount:'always', so not persisting them only costs a cold-offline
// boot showing the default until the first refetch — never data loss.
/**
 * Keys the last dehydrate refused, newest first. F114: `isPersistSafe`
 * fails CLOSED and says nothing, so "Prepare for offline" reported success
 * while a lookup went unsaved and the cold-offline boot in the orchard met
 * an empty picker with no explanation anywhere.
 *
 * Measured on prod 21 Sep 2026: nothing is close to the budget — the largest
 * farm has 38 boundary-bearing blocks (the audit put the threshold at
 * ~60–190) and the largest register 442 observations (threshold ~624). This
 * is therefore a WATCH, not a fix: the day a farm draws its 60th block, the
 * prep screen says which lookup it could not save instead of leaving someone
 * to find it in the orchard.
 */
const refusals = new Map<string, number>()

/** Record a refusal. Called from the dehydrate predicate, which has the key. */
export function notePersistRefusal(queryKey: readonly unknown[]): void {
  const head = String(queryKey?.[0] ?? 'unknown')
  refusals.set(head, Date.now())
}

/** Query-key heads refused within the last `withinMs` (default 5 min). */
export function recentPersistRefusals(withinMs = 5 * 60_000, now = Date.now()): string[] {
  return [...refusals.entries()]
    .filter(([, at]) => now - at <= withinMs)
    .map(([head]) => head)
    .sort()
}

/** The refused heads that belong to the prep's OWN lookups. */
export function refusalsOwnedBy(refused: readonly string[], prepHeads: readonly string[]): string[] {
  const own = new Set(prepHeads)
  return refused.filter(h => own.has(h))
}

/** Testing seam — the module-level map would otherwise leak between cases. */
export function clearPersistRefusals(): void { refusals.clear() }

/**
 * The cache-to-disk rule (main.tsx's PersistQueryClient shouldDehydrateQuery).
 * A query is saved only when it succeeded, has not opted out, and its data
 * round-trips through JSON (isPersistSafe; a refusal is noted for the
 * offline-prep screen, F114).
 *
 * meta.noPersist is the opt-out for a read that must never reach the disk:
 * the PIN-locked Profit tab's numbers (mig 20261045500000) live only in
 * memory, so locking the tab or closing the app leaves nothing in IndexedDB.
 */
export function shouldPersistQuery(q: {
  queryKey: readonly unknown[]
  state:    { status: string; data: unknown }
  meta?:    Record<string, unknown>
}): boolean {
  if (q.state.status !== 'success' || q.meta?.noPersist === true) return false
  if (isPersistSafe(q.state.data)) return true
  notePersistRefusal(q.queryKey)
  return false
}

export function isPersistSafe(value: unknown): boolean {
  let budget = 20_000
  const onPath = new Set<object>()
  const walk = (v: unknown): boolean => {
    if (--budget <= 0) return false
    if (v === null || v === undefined) return true
    const t = typeof v
    if (t === 'string' || t === 'number' || t === 'boolean') return true
    if (t === 'function' || t === 'bigint' || t === 'symbol') return false
    if (v instanceof Map || v instanceof Set || v instanceof Date || ArrayBuffer.isView(v)) return false
    if (Array.isArray(v)) {
      for (const item of v) if (!walk(item)) return false
      return true
    }
    if (t === 'object') {
      const o = v as object
      if (onPath.has(o)) return false                       // true cycle → JSON throws
      const proto = Object.getPrototypeOf(o)
      if (proto !== Object.prototype && proto !== null) return false  // class instance → prototype lost
      onPath.add(o)
      for (const k of Object.keys(o)) {
        if (!walk((o as Record<string, unknown>)[k])) { onPath.delete(o); return false }
      }
      onPath.delete(o)
      return true
    }
    return false
  }
  return walk(value)
}

